// src/services/pushNotifications.ts
import { Capacitor } from "@capacitor/core";
import { PushNotifications, type Token } from "@capacitor/push-notifications";
import { Preferences } from "@capacitor/preferences";
import { supabase } from "../supabaseClient";

const LAST_TOKEN_KEY = "push_notification_last_token";
let listenersAttached = false;

function currentPlatform(): "ios" | "android" | null {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android" ? p : null;
}

async function saveTokenForCurrentUser(token: string) {
  const platform = currentPlatform();
  if (!platform) return;

  // Toujours mis en cache en premier : si la session Supabase n'est pas encore
  // restaurée à ce moment (cas fréquent juste après un lancement à froid), le
  // token ne doit pas être perdu — resyncPushToken() le renverra dès que
  // l'auth sera prête (voir NotificationsProvider).
  await Preferences.set({ key: LAST_TOKEN_KEY, value: token });

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  await supabase.from("push_tokens").upsert(
    {
      user_id: user.id,
      token,
      platform,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "token" }
  );
}

// À appeler une fois la session utilisateur garantie disponible (ex: sur
// SIGNED_IN) pour rattraper un token reçu avant que l'auth soit prête.
export async function resyncPushToken(): Promise<void> {
  const platform = currentPlatform();
  if (!platform) return;

  const { value: token } = await Preferences.get({ key: LAST_TOKEN_KEY });
  if (!token) return;

  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return;

  await supabase.from("push_tokens").upsert(
    {
      user_id: user.id,
      token,
      platform,
      updated_at: new Date().toISOString(),
    },
    { onConflict: "token" }
  );
}

function attachListenersOnce() {
  if (listenersAttached) return;
  listenersAttached = true;

  PushNotifications.addListener("registration", (token: Token) => {
    void saveTokenForCurrentUser(token.value);
  });

  PushNotifications.addListener("registrationError", (err) => {
    console.warn("[push] registration error", err);
  });
}

export async function registerForPush(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;

  attachListenersOnce();

  const { receive } = await PushNotifications.requestPermissions();
  if (receive !== "granted") return false;

  await PushNotifications.register();
  return true;
}

export async function removePushToken(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  const { value: token } = await Preferences.get({ key: LAST_TOKEN_KEY });
  if (!token) return;

  await supabase.from("push_tokens").delete().eq("token", token);
  await Preferences.remove({ key: LAST_TOKEN_KEY });
}
